/** Options for {@link VisibilityGate}. */
export interface VisibilityGateOptions {
  /** Called once the document has stayed hidden for `graceMs`. */
  onHiddenGraceExpired: () => void;
  /** Called when the document becomes visible after the grace period expired. */
  onVisible: () => void;
  graceMs: number;
  doc?: Document;
}

/**
 * Watches `document.visibilityState` and fires `onHiddenGraceExpired` after the document
 * stays hidden for `graceMs`. Becoming visible before expiry cancels the timer silently;
 * becoming visible after expiry fires `onVisible`.
 */
export class VisibilityGate {
  private readonly onHiddenGraceExpired: () => void;
  private readonly onVisible: () => void;
  private readonly graceMs: number;
  private readonly doc: Document;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private expired = false;

  constructor({ onHiddenGraceExpired, onVisible, graceMs, doc = document }: VisibilityGateOptions) {
    this.onHiddenGraceExpired = onHiddenGraceExpired;
    this.onVisible = onVisible;
    this.graceMs = graceMs;
    this.doc = doc;
  }

  start() {
    this.doc.addEventListener('visibilitychange', this.handleVisibilityChange);
    if (this.doc.visibilityState === 'hidden') {
      this.armTimer();
    }
  }

  stop() {
    this.doc.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.clearTimer();
    this.expired = false;
  }

  private readonly handleVisibilityChange = () => {
    if (this.doc.visibilityState === 'hidden') {
      if (!this.expired) {
        this.armTimer();
      }
      return;
    }
    this.clearTimer();
    if (this.expired) {
      this.expired = false;
      this.onVisible();
    }
  };

  private armTimer() {
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.timer = null;
      this.expired = true;
      this.onHiddenGraceExpired();
    }, this.graceMs);
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
